@file:OptIn(ExperimentalAtomicApi::class)

package org.maplibre.compose.style

import kotlin.concurrent.atomics.AtomicReference
import kotlin.concurrent.atomics.ExperimentalAtomicApi
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import org.maplibre.compose.sources.GeometryTileProvider
import org.maplibre.compose.sources.VectorTileProvider

/** A source installed in exactly one loaded-style generation. */
internal class SourceInstallation(
  private val style: StyleBinding,
  definition: SourceDefinition,
) {
  private val current = AtomicReference(definition)
  private val forwardingGeometryProvider = GeometryTileProvider { tile ->
    (current.load() as SourceDefinition.CustomGeometry).provider.loadTile(tile)
  }
  private val forwardingVectorProvider = VectorTileProvider { tile ->
    (current.load() as SourceDefinition.CustomVector).provider.loadTile(tile)
  }

  val id: String = definition.id
  val definition: SourceDefinition
    get() = current.load()

  /** The engine's outcome for adding this source. */
  val added: StyleCommand = style.addSource(definition.forInstallation())

  fun update(definition: SourceDefinition) {
    style.requireCurrent()
    require(definition.id == id) { "A source handle cannot change resource identity" }
    val previousDefinition = current.load()
    if (definition == previousDefinition) return
    when {
      previousDefinition is SourceDefinition.GeoJson && definition is SourceDefinition.GeoJson -> {
        require(previousDefinition.options == definition.options) {
          "GeoJSON source options cannot change without replacing source '$id'"
        }
        style.submitGeoJsonData(id, definition.data, definition.options)
      }
      previousDefinition is SourceDefinition.Image && definition is SourceDefinition.Image -> {
        val previous = previousDefinition
        if (previous.coordinates != definition.coordinates) {
          style.setImageSourceCoordinates(id, definition.coordinates)
          // Content can fail after these corners were accepted. Keep their committed value so a
          // later revision can restore them, including a return to the original definition.
          current.store(
            previous.copy(
              coordinates = definition.coordinates,
              value =
                JsonObject(
                  previous.value + ("coordinates" to definition.value.getValue("coordinates")),
                ),
            ),
          )
        }
        if (
          previous.image != definition.image || previous.value["url"] != definition.value["url"]
        ) {
          definition.image?.let { style.setImageSourceImage(id, it) }
            ?: style.setImageSourceUrl(
              id,
              (definition.value["url"] as? JsonPrimitive)?.content.orEmpty(),
            )
        }
      }
      previousDefinition is SourceDefinition.CustomGeometry &&
        definition is SourceDefinition.CustomGeometry -> {
        require(previousDefinition.options == definition.options) {
          "Custom geometry source options cannot change without replacing source '$id'"
        }
      }
      previousDefinition is SourceDefinition.CustomVector &&
        definition is SourceDefinition.CustomVector -> {
        require(previousDefinition.options == definition.options) {
          "Custom vector source options cannot change without replacing source '$id'"
        }
      }
      else -> error("Source '$id' changed type or immutable options while it was installed")
    }
    current.store(definition)
  }

  fun remove(): StyleCommand {
    style.requireCurrent()
    style.identity.sources.remove(id)
    return style.removeSource(id)
  }

  private fun SourceDefinition.forInstallation(): SourceDefinition =
    when (this) {
      is SourceDefinition.CustomGeometry -> copy(provider = forwardingGeometryProvider)
      is SourceDefinition.CustomVector -> copy(provider = forwardingVectorProvider)
      else -> this
    }
}

/**
 * A layer installed in exactly one loaded-style generation. The animator duration scale scales the
 * layer's paint transitions for the engine; a changed scale rewrites them on the next update.
 */
internal class LayerInstallation(
  private val style: StyleBinding,
  definition: LayerDefinition,
  beforeLayerId: String,
  animatorDurationScale: Float = 1f,
) {
  val id: String = definition.id
  var definition: LayerDefinition = definition
    private set

  private var durationScale = animatorDurationScale
  private var current = definition.resolveFor(style, animatorDurationScale)
  private val reportedUnsupported = mutableSetOf<String>()

  /** The engine's outcome for adding this layer. */
  val added: StyleCommand

  init {
    style.requireCurrent()
    added = style.addLayer(current, beforeLayerId)
    reportUnsupported(definition)
  }

  /**
   * Applies the property writes that bring the installed layer from its current definition to
   * [definition], as one batch. A rejected write is the engine keeping the previous value, so
   * [current] advances regardless.
   */
  fun update(definition: LayerDefinition, animatorDurationScale: Float = 1f) {
    style.requireCurrent()
    require(definition.id == id) { "A layer handle cannot change resource identity" }
    if (definition == this.definition && animatorDurationScale == durationScale) return
    val next = definition.resolveFor(style, animatorDurationScale)
    if (next == current) {
      this.definition = definition
      durationScale = animatorDurationScale
      reportUnsupported(definition)
      return
    }
    val writes = buildList {
      collectProperties(
        current["layout"] as? JsonObject,
        next["layout"] as? JsonObject,
        LayerPropertyKind.Layout,
      )
      collectProperties(
        current["paint"] as? JsonObject,
        next["paint"] as? JsonObject,
        LayerPropertyKind.Paint,
      )
      RootPropertyNames.forEach { name ->
        val previous = current[name]
        val value = next[name]
        if (previous == value) return@forEach
        add(
          LayerPropertyWrite(
            id,
            this@LayerInstallation.definition.type,
            name,
            value ?: clearingValue(LayerPropertyKind.Root, name),
            LayerPropertyKind.Root,
          ),
        )
      }
    }
    style.setLayerProperties(writes)
    current = next
    this.definition = definition
    durationScale = animatorDurationScale
    reportUnsupported(definition)
  }

  fun remove(): StyleCommand {
    style.requireCurrent()
    style.identity.layers.remove(id)
    return style.removeLayer(id)
  }

  fun move(beforeLayerId: String): StyleCommand {
    style.requireCurrent()
    return style.moveLayer(id, beforeLayerId)
  }

  /** Describes this layer in a warning about a write the engine rejected. */
  fun describe(): String =
    "Layer '$id' of type '${definition.type}'" +
      (definition.sourceId?.let { " over source '$it'" } ?: "")

  private fun MutableList<LayerPropertyWrite>.collectProperties(
    previous: JsonObject?,
    next: JsonObject?,
    kind: LayerPropertyKind,
  ) {
    // A transition goes before the value it times, so an engine that updates between the two
    // writes animates the new value with the new timing.
    val names = previous.orEmpty().keys + next.orEmpty().keys
    names
      .sortedByDescending { it.endsWith(TransitionSuffix) }
      .forEach { name ->
        val oldValue = previous?.get(name)
        val newValue = next?.get(name)
        if (oldValue == newValue) return@forEach
        add(
          LayerPropertyWrite(
            id,
            this@LayerInstallation.definition.type,
            name,
            newValue ?: clearingValue(kind, name),
            kind,
          ),
        )
      }
  }

  private fun reportUnsupported(definition: LayerDefinition) {
    definition.unsupportedProperties.forEach { (name, reason) ->
      if (reportedUnsupported.add(name)) {
        style.logger?.w { "Layer '$id' of type '${definition.type}' cannot set '$name': $reason" }
      }
    }
    if (!definition.filterUnsupportedProperties) return
    listOf("layout", "paint").forEach { section ->
      (definition.value[section] as? JsonObject)?.forEach { (name, value) ->
        val reason = style.unsupportedLayerPropertyReason(definition.type, name)
        if (reason != null && value !is JsonNull && reportedUnsupported.add(name)) {
          style.logger?.w {
            "Layer '$id' of type '${definition.type}' cannot set '$name': $reason"
          }
        }
      }
    }
  }

  private companion object {
    val RootPropertyNames = setOf("source-layer", "minzoom", "maxzoom", "filter")
  }
}

/**
 * The value that removes [name]. MapLibre Native rejects a null transition and keeps the previous
 * one, so a transition that goes away is cleared with an empty object.
 */
private fun clearingValue(kind: LayerPropertyKind, name: String): JsonElement =
  when {
    kind == LayerPropertyKind.Paint && name.endsWith(TransitionSuffix) -> ClearedTransition
    kind == LayerPropertyKind.Root && name == "minzoom" -> JsonPrimitive(0)
    kind == LayerPropertyKind.Root && name == "maxzoom" -> JsonPrimitive(24)
    else -> JsonNull
  }

/** Applies compatibility filtering and the system animation-duration scale. */
private fun LayerDefinition.resolveFor(
  style: StyleBinding,
  animatorDurationScale: Float,
): JsonObject {
  fun JsonObject.withoutUnsupported(): JsonObject =
    if (filterUnsupportedProperties)
      JsonObject(filterKeys { style.unsupportedLayerPropertyReason(type, it) == null })
    else this

  val resolved = value.toMutableMap()
  (resolved["layout"] as? JsonObject)?.withoutUnsupported()?.let {
    if (it.isEmpty() && filterUnsupportedProperties) resolved.remove("layout")
    else resolved["layout"] = it
  }
  (resolved["paint"] as? JsonObject)
    ?.withoutUnsupported()
    ?.withScaledTransitions(animatorDurationScale)
    ?.let {
      if (it.isEmpty() && filterUnsupportedProperties) resolved.remove("paint")
      else resolved["paint"] = it
    }
  return JsonObject(resolved)
}
